import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import useSWR from 'swr';
import { AdminTableColumn, columns, defaultParams, fields } from './Admin.consts';
import { AppContext } from 'providers/AppProvider';
import { isValidKennitala, QueryString } from 'helpers';
import { PROCESS_STATUS } from 'consts';
import { ProcessesResponse } from 'types';

export function useAdmin() {
	const [params, setParams] = useState(defaultParams);
	const ctx = useContext(AppContext);
	const url = ctx.config.API.getProcesses + QueryString(params);
	const { error, isLoading, ...swr } = useSWR<ProcessesResponse[]>(url);
	const [sortDirection, setSortDirection] = useState({ created: -1, modified: -1 });
	const [data, setData] = useState(swr.data);
	const parentRef = useRef(null);

	const virtualizer = useVirtualizer({
		getScrollElement: () => parentRef.current,
		estimateSize: () => 63,
		count: data ? data.length : 0,
		overscan: 8
	});

	const sortColumn = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			if (Array.isArray(data)) {
				const field = e.target.dataset.field as keyof ProcessesResponse;
				if ('created' === field || 'modified' === field) {
					const direction = sortDirection[field];
					data.sort((a, b) => Compare(a, b, field, direction));
					setSortDirection({ ...sortDirection, [field]: direction * -1 });
					setData([...data]);
				}
			}
		},
		[data, sortDirection]
	);

	const sortable: any = { created: 1, modified: 1 };

	const onChange = useCallback(
		(e: any) => {
			const { name, value } = e.target;
			switch (name) {
				case 'agreementProcessId': {
					if ('' === value || 36 === value.length) {
						setParams({ ...params, [name]: value });
					}
					break;
				}
				case 'processKennitala':
				case 'initiatorsKennitala': {
					if ('' === value || isValidKennitala(value)) {
						setParams({ ...params, [name]: value });
					}
					break;
				}
			}
		},
		[params]
	);

	useEffect(() => {
		Array.isArray(swr.data) && setData(swr.data);
	}, [swr.data]);

	useEffect(() => {
		swr.mutate();
	}, []); // eslint-disable-line

	return {
		...ctx,
		columns,
		data,
		error,
		fields,
		formatValue,
		isLoading,
		onChange,
		parentRef,
		sortable,
		sortColumn,
		virtualizer
	};
}

function formatValue(row: ProcessesResponse, field: AdminTableColumn, t: Function) {
	if ('flowType' === field) {
		return t(`flow-type-${row[field]}`) ?? row[field];
	}
	if ('processStatus' === field) {
		return t(PROCESS_STATUS[row[field]]);
	}
	if ('created' === field || 'modified' === field) {
		return null === row[field] ? '' : new Date(row[field]).toLocaleString();
	}
	return row[field];
}

function Compare(row: ProcessesResponse, next: ProcessesResponse, field: keyof ProcessesResponse, direction: number) {
	if (null === row[field]) return -1;
	const a = new Date(row[field]);
	const b = new Date(next[field]);
	if (a < b) return direction;
	if (a > b) return direction * -1;
	return 0;
}
